import { isProxy, toRaw } from 'vue'

type ErrorWithCause = Error & { cause?: unknown }

const isBlobLike = (value: unknown): value is Blob =>
  typeof Blob !== 'undefined' && value instanceof Blob

const isFileLike = (value: unknown): value is File =>
  typeof File !== 'undefined' && value instanceof File

export function toPlainData<T>(value: T): T {
  if (value === null || value === undefined) return value
  if (typeof value !== 'object') return value

  const rawValue = isProxy(value) ? toRaw(value) : value

  if (rawValue === null) return rawValue as T
  if (rawValue instanceof Date) return new Date(rawValue.getTime()) as T
  if (isFileLike(rawValue) || isBlobLike(rawValue)) return rawValue

  if (rawValue instanceof Error) {
    const cause = (rawValue as ErrorWithCause).cause
    return {
      name: rawValue.name,
      message: rawValue.message,
      stack: rawValue.stack,
      cause: cause !== undefined ? toPlainData(cause) : undefined,
    } as T
  }

  if (Array.isArray(rawValue)) {
    return rawValue.map((entry) => toPlainData(entry)) as T
  }

  const plainObject: Record<string, unknown> = {}
  for (const [key, entry] of Object.entries(rawValue)) {
    plainObject[key] = toPlainData(entry)
  }

  return plainObject as T
}
